export const BANNER_MANIFEST_URL =
  "https://island-image.oss-cn-beijing.aliyuncs.com/banner/manifest.json";

export interface BannerItem {
  id: string;
  artistId: string;
  artistName: string;
  imageUrl: string;
  placements: Array<"sidebar" | "welcome">;
  weight: number;
  enabled: boolean;
}

export interface BannerManifest {
  version: number;
  fallbackId: string;
  sidebar: { rotation: "daily" };
  welcome: { enabled: boolean; campaignId: string };
  items: BannerItem[];
}

export const FALLBACK_BANNER: BannerItem = {
  id: "k4zb5Iu-10",
  artistId: "k4zb5Iu",
  artistName: "岛民 k4zb5Iu",
  imageUrl:
    "https://island-image.oss-cn-beijing.aliyuncs.com/banner/k4zb5lu_10.jpg",
  placements: ["sidebar", "welcome"],
  weight: 1,
  enabled: true,
};

const DAILY_SELECTION_KEY = "islander.banner.daily.v1";
const ALLOWED_IMAGE_HOST = "island-image.oss-cn-beijing.aliyuncs.com";

function isBannerItem(value: unknown): value is BannerItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<BannerItem>;
  try {
    const imageUrl = new URL(item.imageUrl || "");
    return (
      typeof item.id === "string" &&
      typeof item.artistId === "string" &&
      typeof item.artistName === "string" &&
      imageUrl.protocol === "https:" &&
      imageUrl.hostname === ALLOWED_IMAGE_HOST &&
      Array.isArray(item.placements) &&
      typeof item.weight === "number" &&
      item.weight > 0 &&
      item.enabled === true
    );
  } catch (_) {
    return false;
  }
}

export async function fetchBannerManifest(): Promise<BannerManifest> {
  const response = await fetch(BANNER_MANIFEST_URL, { cache: "no-cache" });
  if (!response.ok) throw new Error(`Banner manifest HTTP ${response.status}`);
  const manifest = (await response.json()) as BannerManifest;
  if (
    !manifest ||
    typeof manifest.version !== "number" ||
    !Array.isArray(manifest.items)
  ) {
    throw new Error("Invalid banner manifest");
  }
  manifest.items = manifest.items.filter(isBannerItem);
  return manifest;
}

function localDateKey(now: Date): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function hash(text: string): number {
  let value = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 16777619);
  }
  return value >>> 0;
}

function weightedPick(items: BannerItem[], seed: number): BannerItem {
  const total = items.reduce((sum, item) => sum + item.weight, 0);
  let cursor = (seed / 0xffffffff) * total;
  for (const item of items) {
    cursor -= item.weight;
    if (cursor <= 0) return item;
  }
  return items[items.length - 1];
}

export function selectDailySidebarBanner(
  manifest: BannerManifest,
  now = new Date(),
): BannerItem {
  const candidates = manifest.items.filter(
    (item) => item.enabled && item.placements.includes("sidebar"),
  );
  if (!candidates.length) return FALLBACK_BANNER;

  const date = localDateKey(now);
  try {
    const saved = JSON.parse(localStorage.getItem(DAILY_SELECTION_KEY) || "null");
    const match = candidates.find((item) => item.id === saved?.itemId);
    if (saved?.date === date && match) return match;
  } catch (_) {
    // A damaged local preference must not hide the banner.
  }

  const selected = weightedPick(candidates, hash(`${date}:${manifest.version}`));
  try {
    localStorage.setItem(
      DAILY_SELECTION_KEY,
      JSON.stringify({ date, itemId: selected.id }),
    );
  } catch (_) {
    // Deterministic selection still keeps the image stable when storage is blocked.
  }
  return selected;
}
